import { hasPaymentProblem } from "utils/planStatus";

export interface IHomePageImageItem {
  action: string;
  cfg: string;
  created_at: string;
  created_date: string;
  id: number;
  image_path: string;
  is_liked: boolean;
  negative_prompt: string;
  process: string;
  prompt: string;
  seed: string;
  user_id: number;
}

export interface IUserRegisterRequest {
  name: string;
  password: string;
  confirm_password: string;
  work?: string;
  email: string;
  referral?: string;
  price_id?: string;
  product_id?: string;
}

export interface IUserEditRequest {
  name?: string;
  work?: string;
  // Send `null` to clear the photo — the BE treats an empty string as "no change" and keeps the old one.
  profile_photo?: string | null;
  // REF-5756: UI theme reporting for backoffice adoption analysis. `theme_preference` is the
  // value the user picked in the toggle; `theme_effective` is what they actually render
  // ("system" resolved via OS). BE persists both on `PUT users/{id}`.
  theme_preference?: "light" | "dark" | "system";
  theme_effective?: "light" | "dark";
}

export interface IUserLoginRequest {
  username: string;
  password: string;
  client_id: string;
  otp: string;
}

export interface IUserOTPLoginRequest {
  username: string;
  password: string;
}

export interface IUserInfoRequest {
  user_id: string;
}

export interface IHistoryResponse {
  uid: string;
}

export interface ICreatedHistoryRequest {
  uid: string;
}

export interface IEditProfileRequest {
  email: string;
  name: string;
  work: string;
}

export interface IChangePasswordRequest {
  password: string;
}

export interface ILikeRequest {
  object_type: string;
  object_id: string;
}

export interface IGoogleLogin {
  // google_token is the signed Google id_token (GIS `credential` JWT), not the `sub`.
  // The backend derives the trusted email from the verified token, so email is not sent.
  google_token: string;
  client_id: string;
}

export interface INotifInfo {
  notif_type: string;
  notif_message: string;
  notif_status: number;
  notif_modal_type?: import("components/PackageUpgradeModal/PackageUpgradeModal.type").UpgradeScenario;
}

export interface IBonusCredit {
  action_code: string;
  remaining: number;
  // "YYYY-MM-DD HH:MM:SS", implicit UTC (BE container default — confirmed with BE dev, REF-5365).
  expires_at: string | null;
}

export interface IUserDetailResponse {
  user_info: {
    id: string;
    name: string;
    email: string;
    username?: string;
    work?: string;
    profile_photo: string | null;
    privacy_perm?: number;
    package_type?: string;
    end_try_date?: string | null;
    end_date?: string | null;
    language?: "en" | "tr" | null;
    // The PLATFORM STAFF flag (DB column `users.is_superadmin`), granted by hand from the back
    // office — NOT "the subscription owner", which is what this comment used to imply. Note the
    // spelling: `is_superadmin` (no underscore), inside `user_info`; the top-level `is_super_admin`
    // below is a different thing. Since BE REF-6290 this is not the Usage gate — it is only an
    // additive bypass inside it, so gate features on `can_manage_billing` and treat this as a
    // fallback that keeps already-granted accounts working.
    is_superadmin?: boolean;
    demo_info?: {
      is_design: boolean;
      is_mannequin: boolean;
      is_video: boolean;
      is_techpack: boolean;
      is_trend: boolean;
      end_try_date: string;
    };
  };
  package_detail: Array<{
    package_type: string;
    code: string;
    name: string;
    end_date: string;
    total_price: number;
    // v3 account plan lines (`billing/domain/account_view.py` `PlanLine.as_dict`). `subscription_status` is the raw Stripe
    // status (active / trialing / past_due — unpaid and incomplete are not listed today); `canceled_at` is Stripe's
    // `cancel_at`, i.e. a SCHEDULED cancellation (cleared on resume); both strings are Postgres-rendered timestamps.
    // Presentation rules: `utils/planStatus`.
    subscription_status?: string | null;
    billing_type?: string | null;
    canceled_at?: string | null;
    product_id?: string | null;
    price_id?: string | null;
    created_at?: string | null;
    // "stripe" | "manual" (backoffice demo / agreement / comp). Only "stripe" is Stripe-billed — `billing_type: "paid"` also
    // appears on manual rows. Never word a manual row as a renewal or charge (utils/planStatus).
    billing_source?: "stripe" | "manual" | string | null;
    // STRIPE P5 / PD1 (v3 P5-B1): a change scheduled for the period end (downgrade, seat decrease, shorter interval). The row
    // still describes the CURRENT plan until `effective_at` (utils/planStatus → "changes on").
    scheduled_change?: { effective_at?: string | null } | null;
    // P7 F2-B6 (all additive; absent on an older backend → today's presentation, utils/planStatus). `subscription_id` is
    // what `primary_subscription_id` and `live_plans[]` name. The rest are billing's verdicts for THIS row:
    subscription_id?: string | null;
    // stripe | contract | demo | bridge | gift | extra_credit | trial | addon; null = an unclassified manual row (never
    // guessed as a contract).
    grant_kind?: string | null;
    // A Refabric-managed contract's term end. Sent only while v3's K11 flag (`BILLING_MANAGED_UNTIL`) is on; null otherwise.
    managed_until?: string | null;
    // The scheduled end (Stripe `cancel_at`), same string format as `canceled_at`, which stays for older builds.
    cancel_at?: string | null;
    // true: Stripe will renew it. false: it ends (scheduled end, card-less trial, manual grant). null: unknown → today's text.
    renews?: boolean | null;
    non_renewing_reason?: "user_cancel" | "contract" | "trial_no_card" | string | null;
  }>;
  // P7 F2-B6: every live BASE plan (no trial beside its paid plan, no add-on, no credit top-up), in `package_detail` order.
  // NOT a duplicate verdict by itself — that is `multiple_plans` + per-entry `counts_as_duplicate` (the F1 double-plan rule).
  live_plans?: ILivePlan[];
  primary_subscription_id?: string | null;
  multiple_plans?: boolean;
  // P7 F2-B6: the team's oldest failed open invoice. DETAIL only — whether the account is in a payment problem is still the
  // rows' `subscription_status` (`hasPaymentProblem`). `hosted_invoice_url` is sent only to a viewer who can manage billing.
  payment_problem?: IPaymentProblem | null;
  credit_info?: Record<
    string,
    {
      title: string;
      used?: number;
      total?: number;
      // REF-5549 (BE REF-5446): set on refabric_credits for non-admin team members — total/used
      // then reflect the member's OWN cap/spend, not the team pool. Absent for admins/solo users.
      is_member_cap?: boolean;
    }
  >;
  // One row per action_code — the BE already aggregates stacked add-on purchases
  // (SUM of remaining, earliest expiry). Render as-is; do not re-sum. Empty/absent = no extra credits.
  bonus_credits?: IBonusCredit[];
  used_info?: Record<string, unknown>;
  permission_info?: import("types/permissions").ModulePermissions;
  stripe_portal_url?: string;
  notif_info?: INotifInfo[];
  // Both are computed per request by the BE (services/users.py, via modules.billing.authz) — the
  // REF-5611 note that `is_super_admin` "comes back false even for real owners" predates that and
  // is no longer true.
  //   is_super_admin     — STRICT badge: the caller IS the team's subscription owner, no fallback.
  //   can_manage_billing — EFFECTIVE gate (can_manage_team): staff `is_superadmin` -> owner ->
  //                        team_role='Admin' only when no owner is resolvable. This is the one to
  //                        gate UI on; it is what the BE's own gates use.
  is_super_admin?: boolean;
  can_manage_billing?: boolean;
}

export interface ILivePlan {
  subscription_id?: string | null;
  plan_code?: string | null;
  name?: string | null;
  kind?: string | null;
  billing_source?: string | null;
  // `packages.period`: monthly | yearly | onetime | "" — only the first two are labelled.
  interval?: string | null;
  status?: string | null;
  counts_as_duplicate?: boolean;
}

interface IPaymentProblem {
  since?: string | null;
  decline_code?: string | null;
  // `errors:billing_decline_<code>` for v3's fixed allowlist, else `errors:billing_decline_generic` (utils/paymentProblem).
  decline_message_key?: string | null;
  amount_due_minor?: number | null;
  currency?: string | null;
  hosted_invoice_url?: string;
}

/** What the global recovery surfaces (banner, PastDueModal) show beside the payment-problem flag. */
export interface IPaymentProblemDetail {
  declineMessageKey: string | null;
  hostedInvoiceUrl: string | null;
}

const paymentProblemDetail = (problem: IPaymentProblem | null | undefined): IPaymentProblemDetail | null =>
  problem
    ? {
        declineMessageKey: problem.decline_message_key ?? null,
        hostedInvoiceUrl: typeof problem.hosted_invoice_url === "string" && problem.hosted_invoice_url ? problem.hosted_invoice_url : null,
      }
    : null;

export const mapUserDetailToRedux = (responseData: IUserDetailResponse) => ({
  id: responseData?.user_info.id,
  name: responseData?.user_info.name,
  email: responseData?.user_info.email,
  profilePhoto: responseData?.user_info.profile_photo,
  packageCode: responseData?.user_info?.package_type ?? "",
  permissions: responseData?.permission_info,
  privacy_perm: responseData?.user_info?.privacy_perm ?? 0,
  notifInfo: responseData?.notif_info,
  language: responseData?.user_info?.language ?? undefined,
  // STRIPE Phase 3 / D2: a plan row in past_due / unpaid / incomplete. Paid access is already denied by the backend;
  // FE-generated denials (route gate) use this to offer PAYMENT RECOVERY instead of an upgrade. Never grants anything.
  hasPaymentProblem: hasPaymentProblem(responseData?.package_detail),
  // The backend's billing-management verdict (owner → staff → admin fallback); members without it cannot fix payment.
  canManageBilling: responseData?.can_manage_billing === true,
  // P7 F2-B6: the decline reason + pay link. ALWAYS written (null when absent) — `setUser` merges, so leaving the key out
  // would keep a previous response's invoice link on screen.
  paymentProblem: paymentProblemDetail(responseData?.payment_problem),
});

export interface IUpdatePermRequest {
  privacy_perm: number;
}

// P4-F4 (STRIPE Phase 4): `POST /v1/users/reset_password` / `POST /v1/users/verify_email` — the mailed one-time token in the body.
export interface IResetPasswordRequest {
  token: string;
  email: string;
  password: string;
}

export interface IVerifyEmailRequest {
  token: string;
  email: string;
}
